import React, { useState, useMemo } from 'react'
import {
  Avatar, Text, Spacer, Icon, Button, StatusCircle
} from 'components/atoms'
import { PopoverActions, Tooltip } from 'components/molecules'
import styled, { withTheme } from 'styled-components/macro';
import PropTypes from 'prop-types'
import { statusType } from 'types'
import ContentLoader from 'react-content-loader';
import { generateKey, generateReadablePtDate } from 'services/generators'


const Loader = withTheme(({ theme }) => (
  <LoaderWrapper>
    <ContentLoader
      height={56}
      width={200}
      speed={1}
      primaryColor={theme.colors.bgAnimation}
      secondaryColor={theme.colors.bgLighterS}
      title="a carregar histórico..."
    >
      <rect width="200" height="18" />
      <rect x="36" y="24" width="170" height="14" />
    </ContentLoader>
  </LoaderWrapper>
));

const HistoryCommit = ({
  date,
  user,
  actions,
  onContact,
  onPreview,
  isLoading,
}) => {
  const [isHover, setHover] = useState(false)
  const [hasPopover, setHasPopover] = useState(false)

  const handleContact = () => {
    onContact()
  }
  const handlePreview = () => {
    onPreview()
  }

  const isActive = isHover || hasPopover
  const dateComputed = useMemo(() => generateReadablePtDate(date), [date])

  if (isLoading) return <Loader />

  return (
    <Commit
      isActive={isActive}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <Commit.Main>
        <Commit.Top>
          {user.name && (
            <Tooltip tooltip={user.name} placement="top">
              <Avatar
                name={user.name}
                image={user.image}
                modifiers={['xl']}
                iconHeight={14}
              />
              <Spacer left="xs2" />
            </Tooltip>
          )}
          <Text modifiers={['base', 's']}>
            {dateComputed}
          </Text>
        </Commit.Top>
        {actions.length > 0 && (
          <Commit.Bottom>
            {actions.map((action) => (
              <Commit.Action key={generateKey(action.description)}>
                {action.icon && (
                  <Commit.ActionIcon>
                    <Icon icon={action.icon} height={14} />
                  </Commit.ActionIcon>
                )}
                {action.status && (
                  <Commit.ActionIcon>
                    <StatusCircle status={action.status} />
                  </Commit.ActionIcon>
                )}
                {action.description && (
                  <>
                    <Spacer left="xs" />
                    <Commit.Description>
                      <Text modifiers={['s']}>{action.description}</Text>
                      {
                        action.link && (     
                          <Text modifiers={['s']}>
                            {
                              action.linkExpired ? '(link Expirado)' : <a href={action.link} target="_blank" rel="noopener noreferrer"> Ver versão no Portal</a>
                            }
                          </Text>
                        )
                      }
                    </Commit.Description>
                  </>
                )}
              </Commit.Action>
            ))}
          </Commit.Bottom>
        )}
      </Commit.Main>
      <Commit.Handler isActive={isActive}>
        <PopoverActions
          actions={[
            {
              text: 'pré-visualizar',
              onClick: handlePreview,
            },
            ...(typeof onContact === 'function'
              ? [
                {
                  text: 'contactar editor',
                  onClick: handleContact,
                }
              ]
              : []
            ),
          ]}
          handler={(
            <Button modifiers={['xs4', 'invisibleDarker']} aria-label="ver mais ações">
              <Icon icon="more-horiz" height={20} />
            </Button>
          )}
          position="bottom"
          align="end"
          handlerWidth="auto"
          onChangeOpen={(isOpen) => setHasPopover(isOpen)}
        />
      </Commit.Handler>
    </Commit>
  )
}

const Commit = styled.div`
  display: flex;
  justify-content: space-between;
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.xs2};
  transition: background-color 0.2s ease-in-out;
  background-color: ${props => (props.isActive ? props.theme.colors.bg : 'transparent')};

  &:hover {
    background-color: ${props => props.theme.colors.bg};
  }
`

Commit.Main = styled.div`
  display: flex;
  flex-direction: column;
`

Commit.Handler = styled.div`
  display: flex;
  align-items: center;
  transition: opacity 0.2s ease-in-out;
  opacity: ${props => (props.isActive ? '1' : '0')};
`

Commit.Top = styled.div`
  display: flex;
`

Commit.Description = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs4};
`

Commit.Bottom = styled.div`
  padding-left: ${props => props.theme.spacing.xl};
  margin-top: ${props => props.theme.spacing.xs2};
`

Commit.Action = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing.xs3};
  &:last-child {
    margin-bottom: 0px;
  }
`

Commit.ActionIcon = styled.div`
  width: ${props => props.theme.sizes.s};
  display: flex;
  align-items: center;
  justify-content: center;
`

const LoaderWrapper = styled.div`
  width: 200px;
  height: 56px;
  svg {
    width: 100%;
    height: 100%;
  }
`;

HistoryCommit.propTypes = {
  date: PropTypes.string.isRequired,
  user: PropTypes.shape({
    name: PropTypes.string,
    image: PropTypes.string,
  }),
  actions: PropTypes.arrayOf(PropTypes.shape({
    icon: PropTypes.string,
    description: PropTypes.string.isRequired,
    status: statusType,
  })),
  onContact: PropTypes.func,
  onPreview: PropTypes.func,
  isLoading: PropTypes.bool,
}

HistoryCommit.defaultProps = {
  onContact: null,
  onPreview: () => { },
  isLoading: false,
  link: null,
  linkExpired: false,
  user: {
    name: null,
    image: null,
  },
  actions: []
}

export default HistoryCommit
